/**
 * @brisa/ui · Input
 *
 * Primitive base com label, error + aria-describedby, types text/email/password.
 */
import * as React from 'react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  /**
   * Quando true, renderiza botão de toggle visibility para inputs `type="password"`.
   * Ignorado se `type !== 'password'`.
   */
  showToggle?: boolean;
  /** ARIA label do botão de toggle (default em pt) */
  toggleShowLabel?: string;
  toggleHideLabel?: string;
}

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `brisa-input-${idCounter}`;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    error,
    id: idProp,
    className,
    type = 'text',
    disabled,
    showToggle,
    toggleShowLabel = 'Mostrar senha',
    toggleHideLabel = 'Ocultar senha',
    ...rest
  },
  ref,
) {
  const [fallbackId] = React.useState(() => nextId());
  const [revealed, setRevealed] = React.useState(false);
  const id = idProp ?? fallbackId;
  const errorId = error ? `${id}-error` : undefined;

  const isPasswordToggle = showToggle && type === 'password';
  const effectiveType = isPasswordToggle && revealed ? 'text' : type;

  const classes = [
    'brisa-input',
    isPasswordToggle && 'brisa-input--with-toggle',
    error && 'brisa-input--error',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="brisa-input-wrapper">
      {label ? (
        <label htmlFor={id} className="brisa-input-label">
          {label}
        </label>
      ) : null}
      <div className={isPasswordToggle ? 'brisa-input-with-toggle' : undefined}>
        <input
          ref={ref}
          id={id}
          type={effectiveType}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          className={classes}
          {...rest}
        />
        {isPasswordToggle ? (
          <button
            type="button"
            className="brisa-input-toggle"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? toggleHideLabel : toggleShowLabel}
            aria-pressed={revealed}
            tabIndex={-1}
          >
            {revealed ? '👁' : '👁‍🗨'}
          </button>
        ) : null}
      </div>
      {error ? (
        <p id={errorId} className="brisa-input-error-text" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
});

Input.displayName = 'Input';
